import { getCalendarViewTypeMenuItems } from './viewSwitcher/getCalendarViewTypeMenuItems';
import { getSurfaceConfigMenuItems } from './viewSwitcher/getSurfaceConfigMenuItems';
import { getSurfaceCreateSaveViewMenuItems } from './viewSwitcher/getSurfaceCreateSaveViewMenuItems';
import { getSurfaceSavedViewMenuItems } from './viewSwitcher/getSurfaceSavedViewMenuItems';
import { getViewSwitcherSubMenuProps } from './viewSwitcher/getViewSwitcherSubMenuProps';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { assertNever } from 'owa-assert';
import { getSelectedViewType, isBoardViewTypeSelected } from 'owa-calendar-module-store';
import { getNumDaysInDayRange } from 'owa-calendar-surface-options-utils';
import { CalendarViewTypeEnum } from 'owa-calendar-view-type';
import { CharmControl, CharmControlColorPattern } from 'owa-charmcontrol';
import { isFeatureEnabled } from 'owa-feature-flags';
import { DisplayOption, FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import { getPalette } from 'owa-theme';
import * as React from 'react';
import {
    geBoardViewTypeDefinition,
    getDayViewTypeDefinition,
    getMultiDayViewTypeDefinition,
    getMonthViewTypeDefinition,
    getSavedViewTypeDefinition,
    getWeekViewTypeDefinition,
    getWorkWeekViewTypeDefinition,
    ViewTypeDefinition,
} from './viewSwitcher/viewTypeDefinitions';
import {
    lazyHasCurrentlyDisplayedView,
    lazyGetCurrentlyDisplayedViewId,
    lazyGetSavedCalendarView,
} from 'owa-save-calendar-view-data';

const styles = require('../components/CalendarModuleCommandBar.scss');

export function getViewSwitcherMenuItem(onCustomRenderSubMenuChange: () => void) {
    const getIsSavedViewSelected = () => {
        if (isFeatureEnabled('cal-saveCalendarView')) {
            const hasCurrentlyDisplayedView = lazyHasCurrentlyDisplayedView.tryImportForRender();
            if (hasCurrentlyDisplayedView?.()) {
                return true;
            }
        }
        return false;
    };

    const isSavedViewSelected = getIsSavedViewSelected();

    const getSelectedSaveView = () => {
        if (isSavedViewSelected) {
            const getCurrentlyDisplayedViewId = lazyGetCurrentlyDisplayedViewId.tryImportForRender();
            const getSavedCalendarView = lazyGetSavedCalendarView.tryImportForRender();
            if (getCurrentlyDisplayedViewId && getSavedCalendarView) {
                return getSavedCalendarView(getCurrentlyDisplayedViewId());
            }
        }
        return null;
    };

    const selectedSaveViewConfig = getSelectedSaveView();
    const hasSelectedSaveViewConfig: boolean = !!selectedSaveViewConfig;

    const getCurrentViewTypeDefinition = (): ViewTypeDefinition => {
        if (hasSelectedSaveViewConfig) {
            return getSavedViewTypeDefinition(selectedSaveViewConfig);
        }

        const selectedViewType = getSelectedViewType();
        const numDaysInDayRange = getNumDaysInDayRange();
        switch (selectedViewType) {
            case CalendarViewTypeEnum.Daily:
                if (numDaysInDayRange === 1) {
                    return getDayViewTypeDefinition();
                } else {
                    return getMultiDayViewTypeDefinition(numDaysInDayRange);
                }
            case CalendarViewTypeEnum.WorkWeek:
                return getWorkWeekViewTypeDefinition();
            case CalendarViewTypeEnum.Weekly:
                return getWeekViewTypeDefinition();
            case CalendarViewTypeEnum.Monthly:
            case CalendarViewTypeEnum.Agenda: // not currently in use, but providing as graceful fallback
                return getMonthViewTypeDefinition();
            case CalendarViewTypeEnum.Board:
                return geBoardViewTypeDefinition();
            default:
                assertNever(selectedViewType);
        }
    };

    const palette = getPalette();
    const charmColorProps = {
        colorPattern: CharmControlColorPattern.Normal,
        calPrimary: palette.themePrimary,
        calDark: palette.themeDark,
        calDarkAlt: palette.themeDarkAlt,
        textColor: palette.neutralPrimary,
    };

    const viewSwitcherMenuItemList: IContextualMenuItem[] = [
        ...getCalendarViewTypeMenuItems(hasSelectedSaveViewConfig),
        ...getSurfaceSavedViewMenuItems(onCustomRenderSubMenuChange),
    ];

    if (!isBoardViewTypeSelected()) {
        viewSwitcherMenuItemList.push(
            ...getSurfaceConfigMenuItems(hasSelectedSaveViewConfig),
            ...getSurfaceCreateSaveViewMenuItems(hasSelectedSaveViewConfig)
        );
    }

    return new FilterableMenuItem({
        ...getCurrentViewTypeDefinition(),
        [FilterableMenuItemArgs.displayOption]: DisplayOption.Left,
        [FilterableMenuItemArgs.key]: 'ViewSwitcher',
        [FilterableMenuItemArgs.subMenuProps]: getViewSwitcherSubMenuProps(
            viewSwitcherMenuItemList
        ),
        [FilterableMenuItemArgs.onRenderIcon]: hasSelectedSaveViewConfig
            ? () => (
                  <CharmControl
                      iconId={parseInt(selectedSaveViewConfig.viewIcon)}
                      className={styles.charm}
                      colorProps={charmColorProps}
                  />
              )
            : undefined,
    });
}
